import React, { useState, useEffect, useCallback } from 'react';
import { matchVideosService } from '../../services/matchVideosService';
import { videosService } from '../../services/videosService';
import { useAuth } from '../../contexts/AuthContext';
import { parseTimeToSeconds, formatSecondsToMMSS } from '../../utils/videoTimingUtils';

interface VideoInput {
  id: string;
  url: string;
  order: number;
  matchStartVideoTimeSeconds?: number | null;
  matchStartMatchMinute?: number | null;
  storageVideoId?: string | null;
}

interface VideoLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (videoUrls: string[]) => void;
  matchName?: string;
  matchId?: string;
}

const STORAGE_CDN_BASE = 'https://cdn.capture.kridah.com';

const VideoLinkModal: React.FC<VideoLinkModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  matchName,
  matchId
}) => {
  const [selectedOption, setSelectedOption] = useState<'youtube' | 'storage'>('youtube');
  const [isSourceLocked, setIsSourceLocked] = useState(false);
  const [lockedSource, setLockedSource] = useState<'youtube' | 'storage' | null>(null);
  const [videoInputs, setVideoInputs] = useState<VideoInput[]>([
    { id: '1', url: '', order: 1 }
  ]);
  const [expandedTimingSections, setExpandedTimingSections] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingExistingVideos, setLoadingExistingVideos] = useState(false);
  const { user } = useAuth();

  // Fetch existing videos when modal opens
  useEffect(() => {
    const fetchExistingVideos = async () => {
      if (!isOpen || !matchId) return;

      setLoadingExistingVideos(true);
      try {
        const result = await matchVideosService.getMatchVideos(matchId);
        if (result.success && result.data && result.data.length > 0) {
          // Pre-populate with existing videos (including timing fields)
          const inferredSource =
            result.data[0]?.video_source_type === 'cloudflare_r2' ||
            result.data[0]?.storage_video_id
              ? 'storage'
              : 'youtube';
          const normalizedSource = inferredSource === 'storage' ? 'storage' : 'youtube';
          setSelectedOption(normalizedSource);
          setIsSourceLocked(true);
          setLockedSource(normalizedSource);

          const existingVideoInputs: VideoInput[] = result.data.map((video) => ({
            id: `existing-${video.id}`,
            url: video.video_url,
            order: video.video_order,
            matchStartVideoTimeSeconds: video.match_start_video_time_seconds ?? 0,
            matchStartMatchMinute: video.match_start_match_minute ?? 0,
            storageVideoId: video.storage_video_id || null,
          }));

          // Sort by video_order and update inputs
          const sortedInputs = existingVideoInputs.sort((a, b) => a.order - b.order);
          setVideoInputs(sortedInputs);
          
          // Auto-expand advanced options for existing videos so users can see/edit timing
          const existingVideoIds = new Set(sortedInputs.map(v => v.id));
          setExpandedTimingSections(existingVideoIds);
        } else {
          // No existing videos, start with empty input
          setVideoInputs([{ id: '1', url: '', order: 1, matchStartVideoTimeSeconds: 0, matchStartMatchMinute: 0 }]);
          setIsSourceLocked(false);
          setLockedSource(null);
          setSelectedOption('youtube');
        }
        // Reset expanded timing sections when loading new videos
        setExpandedTimingSections(new Set());
      } catch (err) {
        console.error('Error fetching existing videos:', err);
        // Fallback to empty input on error
        setVideoInputs([{ id: '1', url: '', order: 1, matchStartVideoTimeSeconds: 0, matchStartMatchMinute: 0 }]);
      } finally {
        setLoadingExistingVideos(false);
      }
    };

    fetchExistingVideos();
  }, [isOpen, matchId]);

  // URL validation for YouTube and Twitch
  const validateYouTubeUrl = (url: string): boolean => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  const validateVideoUrl = (url: string): boolean => {
    return validateYouTubeUrl(url);
  };

  const parseStorageVideoInput = (inputUrl: string) => {
    if (!inputUrl) return null;
    let normalizedUrl = inputUrl.trim();
    let path = normalizedUrl;
    try {
      if (normalizedUrl.startsWith('http')) {
        const parsed = new URL(normalizedUrl);
        path = parsed.pathname;
      } else {
        path = normalizedUrl;
        normalizedUrl = `${STORAGE_CDN_BASE}/${path.replace(/^\/+/, '')}`;
      }
      const segments = path.replace(/^\/+/, '').split('/');
      if (segments.length < 2) return null;
      const videoId = segments[1];
      const uuidRegex = /^[0-9a-fA-F-]{36}$/;
      if (!uuidRegex.test(videoId)) return null;
      return { url: normalizedUrl, storageVideoId: videoId };
    } catch (error) {
      return null;
    }
  };

  // Look up actual video ID from the videos table by URL
  const lookupStorageVideoId = useCallback(async (url: string): Promise<string | null> => {
    if (!url) return null;
    try {
      const result = await videosService.getVideoByPath(url);
      if (result.success && result.data) {
        return result.data.id;
      }
    } catch (error) {
      console.warn('Error looking up video ID:', error);
    }
    return null;
  }, []);

  const handleVideoUrlChange = async (id: string, url: string) => {
    // Update URL immediately
    setVideoInputs(prev =>
      prev.map(input =>
        input.id === id
          ? { ...input, url, storageVideoId: undefined }
          : input
      )
    );
    setError(null);

    if (selectedOption === 'youtube' && url && !validateVideoUrl(url)) {
      setError(`Please enter a valid YouTube URL`);
      return;
    }

    // Look up actual video ID for storage videos
    if (selectedOption === 'storage' && url) {
      const storageVideoId = await lookupStorageVideoId(url);
      setVideoInputs(prev =>
        prev.map(input =>
          input.id === id
            ? { ...input, storageVideoId }
            : input
        )
      );
    }
  };

  const addVideoInput = () => {
    if (videoInputs.length >= 5) return; // Limit to 5 videos
    
    const newOrder = Math.max(...videoInputs.map(v => v.order)) + 1;
    const newInput: VideoInput = {
      id: Date.now().toString(),
      url: '',
      order: newOrder,
      matchStartVideoTimeSeconds: 0,
      matchStartMatchMinute: 0
    };
    
    setVideoInputs(prev => [...prev, newInput]);
  };

  const toggleTimingSection = (id: string) => {
    setExpandedTimingSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleMatchStartVideoTimeChange = (id: string, value: string) => {
    const seconds = parseTimeToSeconds(value);
    setVideoInputs(prev => prev.map(input => 
      input.id === id 
        ? { ...input, matchStartVideoTimeSeconds: seconds ?? 0 }
        : input
    ));
    setError(null);
  };

  const handleMatchStartMatchMinuteChange = (id: string, value: string) => {
    const minute = value === '' ? null : parseInt(value, 10);
    if (minute !== null && (isNaN(minute) || minute < 0)) {
      return; // Invalid input, don't update
    }
    setVideoInputs(prev => prev.map(input => 
      input.id === id 
        ? { ...input, matchStartMatchMinute: minute ?? 0 }
        : input
    ));
  };

  const removeVideoInput = (id: string) => {
    setVideoInputs(prev => {
      const filtered = prev.filter(input => input.id !== id);
      
      // If all inputs are removed, add an empty one
      if (filtered.length === 0) {
        return [{ id: Date.now().toString(), url: '', order: 1 }];
      }
      
      // Reorder remaining inputs
      return filtered.map((input, index) => ({ ...input, order: index + 1 }));
    });
  };

  const moveVideoInput = (id: string, direction: 'up' | 'down') => {
    setVideoInputs(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const currentIndex = sorted.findIndex(input => input.id === id);
      
      if (direction === 'up' && currentIndex > 0) {
        [sorted[currentIndex], sorted[currentIndex - 1]] = [sorted[currentIndex - 1], sorted[currentIndex]];
      } else if (direction === 'down' && currentIndex < sorted.length - 1) {
        [sorted[currentIndex], sorted[currentIndex + 1]] = [sorted[currentIndex + 1], sorted[currentIndex]];
      }
      
      // Update order numbers
      return sorted.map((input, index) => ({ ...input, order: index + 1 }));
    });
  };

  const handleSourceChange = (option: 'youtube' | 'storage') => {
    if (option === selectedOption) return;
    if (isSourceLocked && option !== lockedSource) {
      setError('Video source is locked for this match. Reset the match to change sources.');
      return;
    }
    setSelectedOption(option);
    setVideoInputs([{ id: '1', url: '', order: 1, matchStartVideoTimeSeconds: 0, matchStartMatchMinute: 0 }]);
    setExpandedTimingSections(new Set());
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!matchId) {
        setError('Match ID is required');
        return;
      }

      if (!user?.id) {
        setError('User authentication required');
        return;
      }

      const nonEmptyInputs = videoInputs.filter(input => input.url.trim());

      let payload: {
        url: string;
        order: number;
        matchStartVideoTimeSeconds?: number | null;
        matchStartMatchMinute?: number | null;
        sourceType?: 'youtube' | 'storage';
        storageVideoId?: string | null;
      }[] = [];

      if (selectedOption === 'youtube') {
        payload = nonEmptyInputs.map(input => ({
          url: input.url.trim(),
          order: input.order,
          matchStartVideoTimeSeconds: input.matchStartVideoTimeSeconds ?? null,
          matchStartMatchMinute: input.matchStartMatchMinute ?? 0,
          sourceType: 'youtube'
        }));

        if (payload.length > 0) {
          for (const input of payload) {
            if (!validateVideoUrl(input.url)) {
              setError('Please enter a valid YouTube URL');
              return;
            }
          }
        }
      } else {
        payload = [];
        for (const input of nonEmptyInputs) {
          const parsed = parseStorageVideoInput(input.url);
          if (!parsed) {
            setError(
              'Enter a valid storage URL or path (e.g. https://cdn.capture.../org_x/<videoId>/master.mp4)'
            );
            setLoading(false);
            return;
          }
          payload.push({
            url: parsed.url,
            order: input.order,
            matchStartVideoTimeSeconds: input.matchStartVideoTimeSeconds ?? null,
            matchStartMatchMinute: input.matchStartMatchMinute ?? 0,
            sourceType: 'storage',
            storageVideoId: parsed.storageVideoId,
          });
        }
      }

      const result = await matchVideosService.replaceMatchVideos(matchId, payload, user.id);

      if (result.success) {
        const videoUrls = payload.map(input => input.url);
        // Dispatch custom event to notify components that videos have been updated
        window.dispatchEvent(new CustomEvent('matchVideosUpdated', { detail: { matchId } }));
        onSuccess(videoUrls);
        onClose();
        setVideoInputs([{ id: '1', url: '', order: 1, matchStartVideoTimeSeconds: 0, matchStartMatchMinute: 0 }]);
      } else {
        setError(result.error || 'Failed to save video links');
      }
    } catch (err) {
      setError('Failed to process video links. Please try again.');
      console.error('Video link submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setVideoInputs([{ id: '1', url: '', order: 1, matchStartVideoTimeSeconds: 0, matchStartMatchMinute: 0 }]);
    setExpandedTimingSections(new Set());
    setError(null);
    setSelectedOption('youtube');
    setIsSourceLocked(false);
    setLockedSource(null);
    onClose();
  };

  if (!isOpen) return null;

  const youtubeLocked = isSourceLocked && lockedSource !== 'youtube';
  const storageLocked = isSourceLocked && lockedSource !== 'storage';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Manage Videos
            </h2>
            {matchName && (
              <p className="text-sm text-gray-500 mt-1">{matchName}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Video Source Options */}
          <div className="space-y-4">
            <div className="text-sm font-medium text-gray-700">Choose video source:</div>
            {isSourceLocked && (
              <p className="text-xs text-amber-600">
                Source locked to {lockedSource === 'storage' ? 'Storage (Capture)' : 'YouTube'} for this match.
                Reset the match to change sources.
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
              {/* YouTube Option */}
              <div 
                className={`flex-1 border-2 rounded-lg p-3 transition-all ${
                  selectedOption === 'youtube' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                } ${youtubeLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => handleSourceChange('youtube')}
              >
                <div className="flex items-center space-x-2">
                  <div
                    className={`w-3 h-3 rounded-full border-2 ${
                      selectedOption === 'youtube' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}
                  >
                    {selectedOption === 'youtube' && <div className="w-1 h-1 bg-white rounded-full m-0.5" />}
                  </div>
                  <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <span className="text-sm font-medium">YouTube</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Paste full YouTube URLs (max 5).</p>
              </div>

              {/* Storage Option */}
              <div 
                className={`flex-1 border-2 rounded-lg p-3 transition-all ${
                  selectedOption === 'storage' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                } ${storageLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => handleSourceChange('storage')}
              >
                <div className="flex items-center space-x-2">
                  <div
                    className={`w-3 h-3 rounded-full border-2 ${
                      selectedOption === 'storage' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}
                  >
                    {selectedOption === 'storage' && <div className="w-1 h-1 bg-white rounded-full m-0.5" />}
                  </div>
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M16 3v4M8 3v4m-5 4h18" />
                  </svg>
                  <span className="text-sm font-medium">Storage (Capture)</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Paste the CDN URL or storage path copied from Capture.
                </p>
              </div>
            </div>
          </div>

          {/* Loading Existing Videos */}
          {loadingExistingVideos && (
            <div className="mt-6 flex items-center justify-center py-4">
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Loading existing videos...</span>
              </div>
            </div>
          )}

          {/* Video URL Inputs */}
          {!loadingExistingVideos && (
            <form onSubmit={handleSubmit} className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  {selectedOption === 'youtube' ? 'YouTube Video URLs' : 'Storage Video URLs'}
                </label>
                <button
                  type="button"
                  onClick={addVideoInput}
                  disabled={videoInputs.length >= 5}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  + Add Video ({videoInputs.length}/5)
                </button>
              </div>

              <div className="space-y-3">
                {videoInputs
                  .sort((a, b) => a.order - b.order)
                  .map((input, index) => (
                    <div key={input.id} className="space-y-2">
                      <div className="flex items-center space-x-2">
                      {/* Drag Handle */}
                      <div className="flex flex-col space-y-1">
                        <button
                          type="button"
                          onClick={() => moveVideoInput(input.id, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => moveVideoInput(input.id, 'down')}
                          disabled={index === videoInputs.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>

                      {/* URL Input */}
                      <div className="flex-1">
                        <input
                          type="text"
                          value={input.url}
                          onChange={(e) => handleVideoUrlChange(input.id, e.target.value)}
                          placeholder={
                            selectedOption === 'youtube'
                              ? 'https://www.youtube.com/watch?v=...'
                              : 'https://cdn.capture.../org_123/<videoId>/master.mp4 or org_123/...'
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>

                      {/* Remove Button - Always show delete icon */}
                      <button
                        type="button"
                        onClick={() => removeVideoInput(input.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Storage helper */}
                    {selectedOption === 'storage' && input.url.trim() && (
                      <div className="ml-8">
                        {input.storageVideoId ? (
                          <p className="text-xs text-green-600">
                            Detected Video ID: {input.storageVideoId}
                          </p>
                        ) : (
                          <p className="text-xs text-red-600">
                            Unable to detect Video ID. Ensure the path includes org_x/&lt;videoId&gt;/...
                          </p>
                        )}
                      </div>
                    )}

                    {/* Advanced Timing Section - Collapsible */}
                    <div className="ml-8 mt-2 border border-gray-200 rounded-md">
                        <button
                          type="button"
                          onClick={() => toggleTimingSection(input.id)}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <span>Advanced Timing (Optional)</span>
                          <svg
                            className={`w-4 h-4 text-gray-500 transition-transform ${expandedTimingSections.has(input.id) ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {expandedTimingSections.has(input.id) && (
                          <div className="px-3 pb-3 space-y-3 border-t border-gray-200 pt-3">
                            {/* Match Start Time in Video */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Match Start Time in Video
                              </label>
                              <input
                                type="text"
                                value={input.matchStartVideoTimeSeconds !== null && input.matchStartVideoTimeSeconds !== undefined
                                  ? formatSecondsToMMSS(input.matchStartVideoTimeSeconds)
                                  : ''}
                                onChange={(e) => handleMatchStartVideoTimeChange(input.id, e.target.value)}
                                placeholder="e.g., 0, 120.5 or 2:00.5"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Leave empty if video is not part of match analysis. Format: MM:SS or seconds (e.g., "120.5" or "2:00.5")
                              </p>
                            </div>
                            
                            {/* Match Minute When Video Starts */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Match Minute When Video Starts
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={input.matchStartMatchMinute ?? 0}
                                onChange={(e) => handleMatchStartMatchMinuteChange(input.id, e.target.value)}
                                placeholder="0 (for full match), 45 (for 2nd half)"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Match minute when this video segment begins (0 for full match, 45 for 2nd half, etc.)
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Supported formats: youtube.com/watch?v=... or youtu.be/...
              </p>

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoLinkModal;